import React from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import i18n from 'i18next';
import { coreActions, coreSelectors } from '@gymcloud/core';
import { getUiState } from 'web-selectors';
import { uiActions } from 'web-actions';
import { Header, AddEntity } from 'web-components';
import { RootState } from 'web-types';

// ─── Types ────────────────────────────────────────────────────────────────────

type Action = {
  icon: string;
  name: string;
  action: () => void;
  isHidden?: boolean;
};

type Props = {
  profile: any;
};

type SearchSectionName = 'clients' | 'exercises' | 'workouts' | 'programs' | 'documents';

type SearchResult = {
  id: number;
  name: string;
  entityType: SearchSectionName;
};

type SearchSection = {
  key: SearchSectionName;
  label: string;
  items: SearchResult[];
};

const SEARCH_SECTIONS: SearchSectionName[] = ['clients', 'exercises', 'workouts', 'programs', 'documents'];

// ─── State selector ───────────────────────────────────────────────────────────

const selectHeaderState = (state: RootState) =>
  getUiState(['namespace', 'namespaceModal'])(state);

// ─── Hooks ────────────────────────────────────────────────────────────────────

function useProfileActions(
  isPro: boolean,
  namespaceSymbol: string,
  navigate: ReturnType<typeof useNavigate>,
  dispatch: ReturnType<typeof useDispatch>,
): Action[] {
  const isIdeafit = namespaceSymbol === 'ideafit';
  const isGymcloud = namespaceSymbol === 'gymcloud';
  const isDev = process.env.TARGET_ENV !== 'production';

  return [
    {
      icon: 'individuals',
      name: 'Profile',
      action: () => navigate('/users/me/edit'),
    },
    {
      icon: 'clients',
      name: i18n.format(i18n.t('client_plural'), 'cap'),
      action: () => navigate('/clients/individuals'),
      isHidden: !isPro,
    },
    {
      icon: 'payment',
      name: 'Payments',
      action: () => navigate('/payments/details'),
      isHidden: !isPro || isIdeafit,
    },
    {
      icon: 'payment',
      name: 'My Account',
      action: () => navigate('/account/details'),
      isHidden: isPro || isGymcloud || isIdeafit,
    },
    {
      icon: 'payment',
      name: 'My Account',
      action: () => navigate('/payments/details'),
      isHidden: !isIdeafit || !isPro,
    },
    {
      icon: 'properties',
      name: 'Set namespace',
      action: () => dispatch(uiActions.setUiState({ namespaceModal: true })),
      isHidden: !isDev,
    },
    {
      icon: 'signout',
      name: 'Sign out',
      action: () => dispatch(coreActions.logoutRequest()),
      isHidden: isIdeafit && isPro,
    },
  ].filter((a) => !a.isHidden);
}

function useAddActions(
  namespaceSymbol: string,
  hasVideoLibrary: boolean,
  navigate: ReturnType<typeof useNavigate>,
  dispatch: ReturnType<typeof useDispatch>,
): Action[] {
  const isIdeafit = namespaceSymbol === 'ideafit';
  const isGymcloud = namespaceSymbol === 'gymcloud';

  return [
    {
      icon: 'client',
      name: i18n.t('add.client'),
      action: () => dispatch(uiActions.setUiState({ addClientModal: true })),
    },
    {
      icon: 'exercise',
      name: i18n.t('add.exercises'),
      action: () =>
        dispatch(
          uiActions.setUiState({
            addEntityModal: {
              isOpen: true,
              folderName: 'Exercises',
              resourceType: 'exercises',
            },
          }),
        ),
    },
    {
      icon: 'workout',
      name: i18n.t('add.workoutTemplates'),
      action: () =>
        dispatch(
          uiActions.setUiState({
            addTemplateModal: {
              isOpen: true,
              folderName: 'Workout Templates',
              resourceType: 'workoutTemplates',
            },
          }),
        ),
    },
    {
      icon: 'program',
      name: i18n.t('add.programTemplates'),
      action: () =>
        dispatch(
          uiActions.setUiState({
            addTemplateModal: {
              isOpen: true,
              folderName: 'Program Templates',
              resourceType: 'programTemplates',
            },
          }),
        ),
    },
    {
      icon: 'video',
      name: i18n.t('add.video'),
      action: () =>
        dispatch(
          uiActions.setUiState({
            uploadVideoModal: { multiple: false, isOpen: true },
          }),
        ),
      isHidden: !isGymcloud && !hasVideoLibrary,
    },
    {
      icon: 'announcement',
      name: i18n.t('add.announcement'),
      action: () => navigate('/new_announcement'),
      isHidden: isIdeafit,
    },
  ].filter((a) => !a.isHidden);
}

function useSearch() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = React.useState('');

  const navigateToSearch = React.useCallback(() => {
    if (!searchQuery.trim()) return;

    const { pathname } = window.location;
    const tab = pathname.startsWith('/search') ? pathname.split('/')[3] : 'all';
    navigate(`/search/${searchQuery.trim()}/${tab}`);
  }, [navigate, searchQuery]);

  const onKeyDown = (e: React.KeyboardEvent<Element>) => {
    if (e.key !== 'Enter') return;
    navigateToSearch();
  };

  return { searchQuery, setSearchQuery, onKeyDown, navigateToSearch };
}

// ─── Component ────────────────────────────────────────────────────────────────

const MainHeader = ({ profile }: Props) => {
  const { namespace, namespaceModal } = useSelector(selectHeaderState, shallowEqual);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isPro = profile.is_pro;
  const avatar = profile.user_profile?.avatar?.thumb.url;
  const { symbol: namespaceSymbol, settings: { features } } = namespace;

  const { searchQuery, setSearchQuery, onKeyDown, navigateToSearch } = useSearch();
  const normalizedSearchQuery = searchQuery.trim();

  const searchSections = useSelector((state: RootState) => {
    if (!normalizedSearchQuery) {
      return [];
    }

    return SEARCH_SECTIONS.reduce((acc, key) => {
      const items = coreSelectors.getSearchItems(key, normalizedSearchQuery)(state)
        .slice(0, 5) as SearchResult[];

      if (!items.length) {
        return acc;
      }

      acc.push({
        key,
        label: key === 'clients' ? i18n.format(i18n.t('client_plural'), 'cap') : i18n.t(key),
        items,
      });

      return acc;
    }, [] as SearchSection[]);
  }, shallowEqual);

  const profileActions = useProfileActions(isPro, namespaceSymbol, navigate, dispatch);
  const addActions = useAddActions(namespaceSymbol, features.video_library, navigate, dispatch);

  const closeNamespaceModal = () =>
    dispatch(uiActions.setUiState({ namespaceModal: false }));

  const handleNamespaceSubmit = (values: { name: string }, callbacks: { setSubmitting: (v: boolean) => void }) => {
    window.localStorage.setItem('namespace', values.name);
    setTimeout(() => {
      callbacks.setSubmitting(false);
      window.location.reload();
    }, 600);
  };

  return (
    <>
      <Header
        isPro={isPro}
        avatar={avatar}
        addActions={addActions}
        profileActions={profileActions}
        namespace={namespace}
        onKeyDown={onKeyDown}
        onSearchSubmit={navigateToSearch}
        searchQuery={searchQuery}
        searchSections={searchSections}
        setSearchQuery={setSearchQuery}
      />
      <AddEntity
        label="Set namespace"
        isOpen={namespaceModal}
        buttonLabel="Set"
        onRequestClose={closeNamespaceModal}
        onSubmit={handleNamespaceSubmit}
      />
    </>
  );
};

export default MainHeader;
